import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Plus, Pencil, Trash2, Upload, X, CheckCircle2, AlertTriangle } from 'lucide-react';
import { adApi } from '../../utils/api';

const POSITION_TARGETS = {
  header: { maxW: 1100, label: 'गृहपृष्ठ माथि (मार्की तल) ~ ११०० पिक्सेल' },
  topright: { maxW: 480, label: 'हेडर दायाँ ~ ४८० पिक्सेल' },
  'sidebar-top': { maxW: 420, label: 'साइडबार माथि ~ ४२० पिक्सेल' },
  'sidebar-bottom': { maxW: 420, label: 'साइडबार तल ~ ४२० पिक्सेल' },
  'article-top': { maxW: 760, label: 'लेख माथि ~ ७६० पिक्सेल' },
  footer: { maxW: 1100, label: 'फुटर ~ ११०० पिक्सेल' },
  inarticle: { maxW: 760, label: 'लेख भित्र ~ ७६० पिक्सेल' },
  infeed: { maxW: 1100, label: 'गृहपृष्ठ फिड (खेलकुद तल) ~ ११०० पिक्सेल' },
  lekh_last: { maxW: 760, label: 'लेखको अन्त्यमा ~ ७६० पिक्सेल' },
  related_top: { maxW: 760, label: 'सम्बन्धित समाचार माथि ~ ७६० पिक्सेल' },
  related_bottom: { maxW: 760, label: 'सम्बन्धित समाचार तल ~ ७६० पिक्सेल' },
  recent_mini: { maxW: 300, label: 'हालै हेरेका समाचार (मिनी) ~ ३०० पिक्सेल' },
  patro_top: { maxW: 900, label: 'पात्रो पेज माथि ~ ९०० पिक्सेल' },
  patro_bottom: { maxW: 900, label: 'पात्रो पेज तल ~ ९०० पिक्सेल' },
  tech_below: { maxW: 1100, label: 'गृहपृष्ठ प्रविधि तल ~ ११०० पिक्सेल' },
};

function processImage(file, maxW) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const origW = img.width;
      const origH = img.height;
      const isGif = file.type === 'image/gif';
      if (isGif || origW <= maxW) {
        URL.revokeObjectURL(url);
        resolve({ file, origW, origH, resized: false, targetW: maxW });
        return;
      }
      const scale = maxW / origW;
      const canvas = document.createElement('canvas');
      canvas.width = maxW;
      canvas.height = Math.round(origH * scale);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve({ file: new File([blob], file.name, { type: file.type }), origW, origH, resized: true, targetW: maxW });
          } else {
            resolve({ file, origW, origH, resized: false, targetW: maxW });
          }
        },
        file.type,
        0.92
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({ file, origW: null, origH: null, resized: false, targetW: maxW });
    };
    img.src = url;
  });
}

function AdminAds() {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', link: '', position: 'sidebar', active: true, image: null });
  const [imagePreview, setImagePreview] = useState(null);
  const [imageInfo, setImageInfo] = useState(null);
  const [originalFile, setOriginalFile] = useState(null);
  const [existingImage, setExistingImage] = useState(null);

  useEffect(() => {
    fetchAds();
  }, []);

  const fetchAds = async () => {
    setLoading(true);
    try {
      const response = await adApi.getAll();
      setAds(response.data);
    } catch (error) {
      console.error('Error fetching ads:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({ title: '', link: '', position: 'sidebar', active: true, image: null });
    setImagePreview(null);
    setImageInfo(null);
    setOriginalFile(null);
    setExistingImage(null);
    setEditing(null);
    setShowForm(false);
  };

  const processForPosition = async (position, file) => {
    const src = file || originalFile;
    if (!src) return;
    const target = POSITION_TARGETS[position] || { maxW: 1200 };
    const result = await processImage(src, target.maxW);
    setForm((f) => ({ ...f, image: result.file }));
    setImagePreview(URL.createObjectURL(result.file));
    setImageInfo(result);
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setOriginalFile(file);
    // File directly pass garne (stale state bug hatauna) — puna-bala re-upload pani kaam garchha
    await processForPosition(form.position, file);
  };

  const handlePositionChange = (e) => {
    const p = e.target.value;
    setForm({ ...form, position: p });
    processForPosition(p);
  };

  const handleSubmit = async () => {
    if (!editing && !form.image) {
      toast.error('विज्ञापनको फोटो छान्नुहोस्।');
      return;
    }
    try {
      const formData = new FormData();
      Object.keys(form).forEach((key) => {
        if (key === 'image' && form[key]) formData.append('adImage', form[key]);
        else if (key !== 'image') formData.append(key, form[key]);
      });
      if (editing) {
        await adApi.update(editing, formData);
        toast.success('विज्ञापन अद्यावधिक भयो।');
      } else {
        await adApi.create(formData);
        toast.success('विज्ञापन थपियो।');
      }
      resetForm();
      await fetchAds();
    } catch (error) {
      console.error('Error saving ad:', error);
      const serverMsg = error.response?.data?.error;
      toast.error(serverMsg ? `विज्ञापन सुरक्षित गर्न असफल भयो। ${serverMsg}` : 'विज्ञापन सुरक्षित गर्न असफल भयो। फोटो (JPG/PNG/GIF/WebP) आवश्यक छ।');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('के तपाईं यो विज्ञापन मेटाउन निश्चित हुनुहुन्छ?')) return;
    try {
      await adApi.delete(id);
      toast.success('विज्ञापन मेटियो।');
      await fetchAds();
    } catch (error) {
      console.error('Error deleting ad:', error);
      toast.error('विज्ञापन मेटाउन असफल भयो।');
    }
  };

  const renderFit = () => {
    if (!imageInfo) return null;
    const { origW, origH, resized, targetW } = imageInfo;
    if (resized) {
      return (
        <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1">
          <AlertTriangle size={13} /> धेरै ठूलो — {targetW}px मा रेसाइज गरियो
        </span>
      );
    }
    if (origW == null) {
      return <span className="text-[12px] text-muted">फोटो जाँच गर्न सकिएन</span>;
    }
    const aspect = origW / origH;
    if (aspect < 0.7 || aspect > 9) {
      return (
        <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-1">
          <AlertTriangle size={13} /> फिट नमिल्न सक्ने आकार ({origW}×{origH})
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-1">
        <CheckCircle2 size={13} /> फिट छ ({origW}×{origH})
      </span>
    );
  };

  if (loading) {
    return <div className="text-center py-16 text-muted">लोड हुँदै...</div>;
  }

  const inputCls = 'w-full border border-line rounded-lg px-3 py-2.5 text-sm font-normal text-ink outline-none bg-white focus:border-crimson';
  const POSITIONS = {
    header: 'गृहपृष्ठ माथि (मार्की तल)',
    topright: 'हेडर दायाँ',
    'sidebar-top': 'साइडबार माथि',
    'sidebar-bottom': 'साइडबार तल',
    'article-top': 'लेख माथि',
    lekh_last: 'लेखको अन्त्यमा',
    related_top: 'सम्बन्धित समाचार माथि',
    related_bottom: 'सम्बन्धित समाचार तल',
    recent_mini: 'हालै हेरेका समाचार (मिनी)',
    patro_top: 'पात्रो पेज माथि',
    patro_bottom: 'पात्रो पेज तल',
    tech_below: 'गृहपृष्ठ प्रविधि तल',
    footer: 'फुटर',
    inarticle: 'लेख भित्र',
    infeed: 'गृहपृष्ठ फिड (खेलकुद तल)'
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
        <h2 className="font-display text-2xl">विज्ञापनहरू</h2>
        <button
          className="inline-flex items-center gap-1.5 bg-crimson text-white font-semibold text-[13.5px] px-4 py-2.5 rounded-full hover:bg-crimson-dark"
          onClick={() => { resetForm(); setShowForm(true); }}
        >
          <Plus size={15} /> नयाँ थप्नुहोस्
        </button>
      </div>

      {showForm && (
        <div className="bg-white border border-line rounded-[14px] p-5 mb-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink-soft">
              विज्ञापनको शीर्षक
              <input className={inputCls} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="विज्ञापनको शीर्षक" />
            </label>
            <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink-soft">
              लिङ्क
              <input className={inputCls} value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="https://..." />
            </label>
            <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink-soft">
              स्थान
              <select className={inputCls} value={form.position} onChange={handlePositionChange}>
                {Object.entries(POSITIONS).map(([k, v]) => (
                  <option key={k} value={k}>{v}</option>
                ))}
              </select>
              <span className="text-[11.5px] text-muted font-normal">
                उपयुक्त आकार: {POSITION_TARGETS[form.position]?.label}
              </span>
            </label>
            <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink-soft">
              सक्रिय
              <select className={inputCls} value={form.active} onChange={(e) => setForm({ ...form, active: e.target.value === 'true' })}>
                <option value="true">सक्रिय</option>
                <option value="false">निष्क्रिय</option>
              </select>
            </label>
            <div className="md:col-span-2 flex flex-col gap-2.5">
              <span className="text-[13px] font-bold text-ink-soft">विज्ञापनको फोटो</span>
              <div className="flex items-start gap-3 flex-wrap">
                {(imagePreview || existingImage) && (
                  <div className="relative w-[120px] h-[80px] rounded-[10px] overflow-hidden border border-line flex-shrink-0">
                    <img src={imagePreview || existingImage} alt="Ad preview" className="w-full h-full object-cover" />
                    {imagePreview && (
                      <button type="button" className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center" onClick={() => { setImagePreview(null); setImageInfo(null); setForm({ ...form, image: null }); }}>
                        <X size={12} />
                      </button>
                    )}
                  </div>
                )}
                <div className="flex flex-col gap-1.5">
                  {renderFit()}
                  {imagePreview && (
                    <span className="text-[11.5px] text-muted">
                      {imageInfo?.origW
                        ? `अपलोड आकार: ${imageInfo.origW}×${imageInfo.origH}px • ${POSITION_TARGETS[form.position]?.label || ''} • ${imageInfo.resized ? 'रेसाइज गरेर सुरक्षित हुनेछ' : 'यही आकारमा सुरक्षित हुनेछ'}`
                        : ''}
                    </span>
                  )}
                </div>
                <label className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-ink-soft border border-dashed border-line rounded-lg px-3 py-2 cursor-pointer hover:border-crimson hover:text-crimson w-fit">
                  <Upload size={14} /> {imagePreview || existingImage ? 'फोटो बदल्नुहोस्' : 'फोटो छान्नुहोस्'}
                  <input type="file" accept="image/*" hidden onChange={handleFileChange} />
                </label>
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2.5 mt-4">
            <button className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-line text-[13px] font-semibold text-ink-soft hover:bg-paper-soft" onClick={resetForm}>
              रद्द गर्नुहोस्
            </button>
            <button className="inline-flex items-center gap-1.5 bg-crimson text-white font-semibold text-[13.5px] px-5 py-2.5 rounded-full hover:bg-crimson-dark" onClick={handleSubmit}>
              {editing ? 'अद्यावधिक गर्नुहोस्' : 'थप्नुहोस्'}
            </button>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-2.5">
        {ads.map((ad) => (
          <div key={ad.id} className="flex items-center gap-3 bg-white border border-line rounded-xl p-3">
            <img src={ad.image || 'https://via.placeholder.com/80x60?text=Ad'} alt="" className="w-20 h-[60px] object-cover rounded-lg flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">{ad.title || 'विज्ञापन'}</p>
              <span className="text-xs text-muted">
                {POSITIONS[ad.position] || ad.position} • {ad.active ? 'सक्रिय' : 'निष्क्रिय'} • क्लिक: {ad.clicks ?? 0} • {ad.link || 'लिङ्क छैन'}
              </span>
            </div>
            <div className="flex gap-1 flex-shrink-0">
              <button
                className="inline-flex items-center justify-center w-9 h-9 rounded-[10px] hover:bg-paper-soft"
                onClick={() => { setEditing(ad.id); setForm({ title: ad.title || '', link: ad.link || '', position: ad.position, active: ad.active, image: null }); setImagePreview(null); setImageInfo(null); setOriginalFile(null); setExistingImage(ad.image || null); setShowForm(true); }}
                aria-label="Edit"
              >
                <Pencil size={16} />
              </button>
              <button className="inline-flex items-center justify-center w-9 h-9 rounded-[10px] hover:bg-red-50 hover:text-crimson" onClick={() => handleDelete(ad.id)} aria-label="Delete">
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
        {ads.length === 0 && <p className="text-muted py-4">कुनै विज्ञापन छैन।</p>}
      </div>
    </div>
  );
}

export default AdminAds;